/** Блок «Без продаж ФБС» под таблицей пополнения (решение 5 задачи fbs-room-stock, 15.10.2026).
 *
 *  Товар, у которого за окно нет продаж ФБС, в расчётную таблицу не попадает — а карточка WB у
 *  него есть и на ГП он лежит (PBF-548). Рекомендации тут нет: количество вводит человек, и
 *  введённое уходит в ТОТ ЖЕ заказ на перемещение (правки — в общих `edits` страницы, строки
 *  в сводку и документ добавляет `fbsSubmit.sendableRows`).
 *
 *  Потолок ввода — свободный остаток ГП строки (`setQty(…, maxQty)`): здесь его не срезал расчёт,
 *  а 409 от 1С после «Создать» — поздно. Ввод сверх — отказ с текстом, а не тихое обрезание.
 */
import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useMemo, useState } from "react";

import type * as api from "@/api/supply";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { DANGER_CLASS, stockNegative } from "./fbsHighlight";
import type { FbsEdits } from "./fbsEdit";
import { NEGATIVE_STOCK_HINT } from "./fbsStock";
import { int0 } from "./format";

/** Бейдж строки — чтобы «без продаж» не путались с расчётными строками таблицы. */
export function NoSalesBadge(): React.ReactElement {
  return (
    <span className="shrink-0 rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 dark:text-sky-300">
      нет продаж
    </span>
  );
}

/** SKU WB без префикса площадки: в блоке все SKU — WB. */
const wbSku = (s: string): string => (s.startsWith("wb:") ? s.slice(3) : s);

export function FbsNoSalesBlock({
  rows,
  edits,
  editError,
  onQty,
}: {
  rows: api.FbsNoSalesRow[];
  edits: FbsEdits;
  /** Ошибка ввода, относящаяся к строке ЭТОГО блока (страница раздаёт ошибки по блокам). */
  editError: { nom: string; text: string } | null;
  onQty: (nomKey: string, raw: string, maxQty: number) => void;
}): React.ReactElement | null {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (needle === "") return rows;
    return rows.filter(
      (r) =>
        r.article.toLowerCase().includes(needle) || r.nom_name.toLowerCase().includes(needle),
    );
  }, [rows, q]);
  const entered = useMemo(() => {
    let qty = 0;
    let lines = 0;
    for (const r of rows) {
      const v = edits[r.nom_key] ?? 0;
      if (v > 0) {
        qty += v;
        lines += 1;
      }
    }
    return { qty, lines };
  }, [rows, edits]);

  if (rows.length === 0) return null;
  return (
    <div className="rounded-md border border-dashed bg-card px-3 py-2.5 text-sm" data-testid="fbs-no-sales">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 text-left font-medium"
      >
        {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        Без продаж ФБС ({rows.length})
        {entered.lines > 0 && (
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            к перемещению{" "}
            <b className="text-foreground">
              {int0(entered.qty)} шт в {entered.lines} стр.
            </b>{" "}
            — уйдут в заказ вместе с таблицей
          </span>
        )}
      </button>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Продаж ФБС за окно нет, но товар есть на ГП и у него есть карточка WB — количество вводится
        вручную.
      </p>

      {open && (
        <div className="mt-2 space-y-2">
          {editError !== null && (
            <p className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-1.5 text-xs text-destructive">
              «К перемещению»: {editError.text} — строка не принята, осталось прежнее число.
            </p>
          )}
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Артикул или товар"
            aria-label="Фильтр «Без продаж ФБС»"
            className="h-8 w-64 text-xs"
          />
          <div className="max-h-[50vh] overflow-auto rounded-md border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 border-b bg-background text-muted-foreground [&>tr]:bg-muted/50">
                <tr>
                  <th className="px-2 py-1 text-left font-medium">Артикул</th>
                  <th className="px-2 py-1 text-left font-medium">Товар</th>
                  <th className="px-2 py-1 text-left font-medium">SKU WB</th>
                  <th className="px-2 py-1 text-right font-medium">Остаток ФБС</th>
                  <th className="px-2 py-1 text-right font-medium">Остаток ГП</th>
                  <th className="px-2 py-1 text-right font-medium">В коробе</th>
                  <th className="w-28 px-2 py-1 text-right font-medium">К перемещению</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const v = edits[r.nom_key];
                  return (
                    <tr key={r.nom_key} className="border-b last:border-0 hover:bg-accent/60">
                      <td className="px-2 py-1">
                        {r.article || <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="max-w-0 px-2 py-1">
                        <span className="flex items-center gap-1.5">
                          <NoSalesBadge />
                          <span className="truncate" title={r.nom_name}>
                            {r.nom_name || <span className="text-muted-foreground">без имени</span>}
                          </span>
                        </span>
                      </td>
                      <td className="px-2 py-1 font-mono text-[11px] text-muted-foreground">
                        {r.mp_skus.map(wbSku).join(", ")}
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums">
                        <span
                          className={stockNegative(r) ? DANGER_CLASS : undefined}
                          title={stockNegative(r) ? NEGATIVE_STOCK_HINT : undefined}
                        >
                          {int0(r.stock_qty)}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums">{int0(r.source_free)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">
                        {r.in_box > 0 ? int0(r.in_box) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-2 py-1">
                        <input
                          value={v === undefined || v === 0 ? "" : String(v)}
                          placeholder="0"
                          aria-label={`К перемещению ${r.article || r.nom_name}`}
                          onChange={(e) => onQty(r.nom_key, e.target.value, r.source_free)}
                          title={`Не больше свободного остатка ГП: ${int0(r.source_free)} шт`}
                          className={cn(
                            "h-6 w-full min-w-0 rounded border bg-background px-1 text-right text-xs tabular-nums focus:border-primary focus:outline-none",
                            editError?.nom === r.nom_key ? "border-destructive" : "border-input",
                          )}
                        />
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-4 text-center text-muted-foreground">
                      Ничего не найдено по фильтру.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
